import { graphqlClient } from '@/lib/graphql-client';
import { postWorkflowWebhook, readWorkflowResponse } from '@/lib/workflow-webhook';
import { dateOnlyRangeClause } from '@/lib/utils/custom-date/custom-date';
import { buildGetToilRequestsQuery, buildGetToilSummaryQuery } from '../graphql/queries';
import {
  GetToilRequestsParams,
  ToilRequest,
  ToilEarnedSummary,
  ToilRequestResult,
  ToilRequestsPagingResult,
  ToilRequestWorkflowInput,
} from '../types/toil.types';

/** Published webhook URL of the TOIL_REQUEST Blocks workflow. */
const TOIL_REQUEST_WEBHOOK_URL = import.meta.env.VITE_TOIL_REQUEST_WEBHOOK_URL ?? '';

/** Published webhook URL of the TOIL_WITHDRAW Blocks workflow. */
const TOIL_WITHDRAW_WEBHOOK_URL = import.meta.env.VITE_TOIL_WITHDRAW_WEBHOOK_URL ?? '';

export const getToilRequests = async (
  params: GetToilRequestsParams
): Promise<{ ToilRequests: ToilRequestsPagingResult }> => {
  const { pageNo, pageSize, filter = {}, order } = params;
  return graphqlClient.query({
    query: buildGetToilRequestsQuery(filter, pageNo, pageSize, order),
  }) as Promise<{ ToilRequests: ToilRequestsPagingResult }>;
};

// A generous upper bound for one employee's TOIL requests in a term (a year of days).
const SUMMARY_PAGE_SIZE = 500;

/**
 * TOIL earned by an employee in an HR term (plain "YYYY-MM-DD" bounds, both inclusive), like the legacy
 * My TOIL summary: every request that isn't rejected, by category. A request without a category is
 * weekend TOIL (special TOIL didn't exist when it was made).
 */
export const getToilEarnedSummary = async (
  userId: string,
  periodStart: string,
  periodEnd: string
): Promise<ToilEarnedSummary> => {
  const result = await graphqlClient.query<{
    ToilRequests: { items: Pick<ToilRequest, 'ToilCategory' | 'Duration'>[] };
  }>({
    query: buildGetToilSummaryQuery(
      {
        UserId: userId,
        Status: { $in: ['Approved', 'Pending'] },
        ToilDate: dateOnlyRangeClause(periodStart, periodEnd),
      },
      SUMMARY_PAGE_SIZE
    ),
  });
  return result.ToilRequests.items.reduce<ToilEarnedSummary>(
    (summary, toil) =>
      toil.ToilCategory === 'SpecialToil'
        ? { ...summary, special: summary.special + (toil.Duration ?? 0) }
        : { ...summary, weekend: summary.weekend + (toil.Duration ?? 0) },
    { weekend: 0, special: 0 }
  );
};

/**
 * Creates a TOIL request through the TOIL_REQUEST workflow, which validates the input, resolves the
 * requester itself, refuses a past day, a work day and a duplicate, and inserts the request. A refusal
 * comes back as `IsSuccessful: false` with the workflow's message.
 */
export const requestToil = async (input: ToilRequestWorkflowInput): Promise<ToilRequestResult> => {
  const response = await postWorkflowWebhook(
    TOIL_REQUEST_WEBHOOK_URL,
    'VITE_TOIL_REQUEST_WEBHOOK_URL',
    { ...input }
  );
  const { IsSuccessful, Message } = readWorkflowResponse(response, 'TOIL_REQUEST_FAILED');
  return { IsSuccessful, Message: IsSuccessful ? '' : Message };
};

/** Withdraws the requester's own pending TOIL request through the TOIL_WITHDRAW workflow. */
export const withdrawToilRequest = async (itemId: string): Promise<ToilRequestResult> => {
  const response = await postWorkflowWebhook(
    TOIL_WITHDRAW_WEBHOOK_URL,
    'VITE_TOIL_WITHDRAW_WEBHOOK_URL',
    { itemId }
  );
  const { IsSuccessful, Message } = readWorkflowResponse(response, 'TOIL_WITHDRAW_FAILED');
  return { IsSuccessful, Message: IsSuccessful ? '' : Message };
};
